import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Smartphone,
  Tag,
  Calendar,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Info,
  Settings
} from 'lucide-react';
import { DispositivoSsem } from '../../interfaces/DispositivoSsemInterface';
import { getDispositivoSsem } from '../../apis/dispositivoSsemApi';

export const DetalleDispositivo: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dispositivo, setDispositivo] = useState<DispositivoSsem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadDispositivo(parseInt(id));
    }
  }, [id]);

  const loadDispositivo = async (dispositivoId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDispositivoSsem(dispositivoId);
      setDispositivo(response.data);
    } catch (error: any) {
      console.error('Error cargando dispositivo:', error);
      setError(error.message || 'Error al cargar el dispositivo');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return new Date(dateTime).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateTime;
    }
  };

  const getStatusColor = (estatus: string) => {
    switch (estatus) {
      case 'activo':
        return { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)' };
      case 'inactivo':
        return { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)' };
      case 'mantenimiento':
        return { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)' };
      case 'perdido':
        return { color: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)', border: 'rgba(220, 38, 38, 0.3)' };
      default:
        return { color: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)' };
    }
  };

  const statusInfo = dispositivo ? getStatusColor(dispositivo.estatus_ssem) : null;

  const styles = `
    .detalle-dispositivo-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1000px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 2rem;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .back-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateX(-2px);
    }

    .page-title {
      font-size: 2rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .header-actions {
      display: flex;
      gap: 1rem;
    }

    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
    }

    .btn-primary {
      background: #007AFF;
      color: white;
    }

    .btn-primary:hover {
      background: #0056CC;
      transform: translateY(-1px);
    }

    .main-content {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 2rem;
    }

    .info-section {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      padding: 2rem;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.2rem;
      font-weight: 600;
      color: #007AFF;
      margin-bottom: 1.5rem;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
    }

    .info-card {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1.5rem;
    }

    .info-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      color: rgba(255, 255, 255, 0.8);
      font-weight: 500;
      font-size: 0.9rem;
    }

    .info-value {
      font-size: 1.1rem;
      font-weight: 600;
      color: white;
      word-break: break-all;
    }

    .info-description {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
      margin-top: 0.5rem;
    }

    .status-card {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      padding: 2rem;
      height: fit-content;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 25px;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 1.5rem;
    }

    .status-indicator {
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }

    .tag-section {
      margin-top: 2rem;
      padding-top: 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .tag-card {
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      padding: 1.5rem;
    }

    .tag-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #007AFF;
      font-weight: 600;
      margin-bottom: 1rem;
    }

    .tag-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }

    .tag-detail {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .tag-label {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .tag-value {
      font-weight: 500;
      color: white;
    }

    .no-tag-message {
      text-align: center;
      padding: 2rem;
      color: rgba(255, 255, 255, 0.6);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 12px;
      border: 1px dashed rgba(255, 255, 255, 0.2);
    }

    .observaciones-section {
      margin-top: 2rem;
    }

    .observaciones-content {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1.5rem;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.6;
      white-space: pre-wrap;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .loading-spinner {
      display: flex;
      align-items: center;
      gap: 1rem;
      color: rgba(255, 255, 255, 0.7);
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .error-container {
      text-align: center;
      padding: 4rem 2rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .error-icon {
      margin: 0 auto 1rem;
      color: #EF4444;
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .main-content {
        grid-template-columns: 1fr;
      }

      .header-section {
        flex-direction: column;
        align-items: stretch;
      }

      .header-actions {
        justify-content: center;
      }

      .info-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="detalle-dispositivo-container">
          <div className="content-wrapper">
            <div className="loading-container">
              <div className="loading-spinner">
                <div className="spinner" />
                Cargando dispositivo...
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !dispositivo) {
    return (
      <>
        <style>{styles}</style>
        <div className="detalle-dispositivo-container">
          <div className="content-wrapper">
            <div className="error-container">
              <AlertCircle className="error-icon" size={48} />
              <h3>Error al cargar dispositivo</h3>
              <p>{error || 'Dispositivo no encontrado'}</p>
              <button onClick={() => navigate('/dispositivos-ssem')} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                <ArrowLeft size={16} />
                Volver a la lista
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="detalle-dispositivo-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <div className="header-left">
              <button onClick={() => navigate('/dispositivos-ssem')} className="back-button">
                <ArrowLeft size={20} />
                Volver
              </button>
              <h1 className="page-title">Detalle del Dispositivo</h1>
            </div>
            <div className="header-actions">
              <Link to={`/dispositivos-ssem/${dispositivo.id}/editar`} className="btn btn-primary">
                <Edit size={16} />
                Editar
              </Link>
            </div>
          </div>

          {/* Contenido Principal */}
          <div className="main-content">
            {/* Información del Dispositivo */}
            <div className="info-section">
              <h2 className="section-title">
                <Smartphone size={24} />
                Información del Dispositivo
              </h2>

              <div className="info-grid">
                <div className="info-card">
                  <div className="info-header">
                    <Settings size={16} />
                    IMEI
                  </div>
                  <div className="info-value">{dispositivo.imei_ssem}</div>
                  <div className="info-description">Identificador único del dispositivo</div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <Smartphone size={16} />
                    Marca y Modelo
                  </div>
                  <div className="info-value">{dispositivo.marca_ssem} {dispositivo.modelo_ssem}</div>
                  <div className="info-description">Información del fabricante</div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <Info size={16} />
                    Color
                  </div>
                  <div className="info-value">{dispositivo.color_ssem}</div>
                  <div className="info-description">Color del dispositivo</div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <Activity size={16} />
                    Almacenamiento
                  </div>
                  <div className="info-value">{dispositivo.capacidad_almacenamiento_ssem}</div>
                  <div className="info-description">Capacidad de almacenamiento</div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <Activity size={16} />
                    Memoria RAM
                  </div>
                  <div className="info-value">{dispositivo.ram_ssem}</div>
                  <div className="info-description">Memoria de acceso aleatorio</div>
                </div>

                <div className="info-card">
                  <div className="info-header">
                    <Calendar size={16} />
                    Fecha de Registro
                  </div>
                  <div className="info-value">{formatDateTime(dispositivo.created_at)}</div>
                  <div className="info-description">Cuando se registró el dispositivo</div>
                </div>
              </div>

              {/* Tag Asignada */}
              <div className="tag-section">
                <h3 className="section-title">
                  <Tag size={20} />
                  Tag de Tracking Asignada
                </h3>

                {dispositivo.tag_blac ? (
                  <div className="tag-card">
                    <div className="tag-header">
                      <CheckCircle size={16} />
                      Tag Activa
                    </div>
                    <div className="tag-info">
                      <div className="tag-detail">
                        <span className="tag-label">Nombre del Dispositivo</span>
                        <span className="tag-value">{dispositivo.tag_blac.name_device}</span>
                      </div>
                      <div className="tag-detail">
                        <span className="tag-label">Nombre/ID</span>
                        <span className="tag-value">{dispositivo.tag_blac.plate}</span>
                      </div>
                      <div className="tag-detail">
                        <span className="tag-label">Fecha de Asignación</span>
                        <span className="tag-value">{formatDateTime(dispositivo.fecha_asignacion_tag)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-tag-message">
                    <AlertCircle size={32} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <h4>Sin Tag Asignada</h4>
                    <p>Este dispositivo no tiene una tag de tracking asignada</p>
                    <Link to={`/dispositivos-ssem/${dispositivo.id}/editar`} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                      <Tag size={16} />
                      Asignar Tag
                    </Link>
                  </div>
                )}
              </div>

              {/* Observaciones */}
              {dispositivo.observaciones && (
                <div className="observaciones-section">
                  <h3 className="section-title">
                    <Info size={20} />
                    Observaciones
                  </h3>
                  <div className="observaciones-content">
                    {dispositivo.observaciones}
                  </div>
                </div>
              )}
            </div>

            {/* Estado y Metadatos */}
            <div className="status-card">
              <h2 className="section-title">
                <Activity size={20} />
                Estado del Dispositivo
              </h2>

              {statusInfo && (
                <div 
                  className="status-badge"
                  style={{
                    color: statusInfo.color,
                    backgroundColor: statusInfo.bg,
                    border: `1px solid ${statusInfo.border}`
                  }}
                >
                  <div 
                    className="status-indicator"
                    style={{ backgroundColor: statusInfo.color }}
                  />
                  {dispositivo.estatus_ssem.charAt(0).toUpperCase() + dispositivo.estatus_ssem.slice(1)}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.25rem' }}>
                    Última Actualización
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {formatDateTime(dispositivo.updated_at)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.25rem' }}>
                    ID del Sistema
                  </div>
                  <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    #{dispositivo.id}
                  </div>
                </div>

                {dispositivo.tag_blac && (
                  <div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.25rem' }}>
                      Estado de Tracking
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      color: '#10B981',
                      fontWeight: 500
                    }}>
                      <CheckCircle size={16} />
                      Activo
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
